import { useState, useEffect, useRef, CSSProperties } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import { styx } from 'modules/Utils';
import useUniversalUploadWidget from 'Hooks/useUniversalUploadWidget';
import useScreen from 'Hooks/useScreen';

interface UploadButtonWithDropdownProps {
    disabled?: boolean;
    className?: string;
    buttonStyle?: CSSProperties;
}

const UploadButtonWithDropdown = ({
    disabled,
    className,
    buttonStyle
}: UploadButtonWithDropdownProps) => {
    const {
        mStyle
    } = useScreen()

    const { loginWithRedirect, isAuthenticated } = useAuth0();
    const { openUniversalUploadWidget } = useUniversalUploadWidget();
    
    // Add CSS animation
    useEffect(() => {
        const styleSheet = document.createElement('style');
        styleSheet.textContent = `
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
        `;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);


    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Check if click is outside dropdown
            if (dropdownRef.current && !dropdownRef.current.contains(target)) {
                // Also check if the click target is not the upload button itself
                const clickedUploadButton = target.closest('.assistantChatUploadButton');
                if (!clickedUploadButton) {
                    setShowDropdown(false);
                }
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showDropdown]);

    const handleImagesVideosClick = () => {
        setShowDropdown(false);
        openUniversalUploadWidget([
            'image/*',
            'video/*'
        ]);
    };

    const handleDocumentsClick = () => {
        if (!isAuthenticated) {
            setShowDropdown(false);
            loginWithRedirect();
        } else {
            setShowDropdown(false);
            openUniversalUploadWidget([
                'application/pdf',
                'text/csv',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'text/plain'
            ]);
        }
    };

    return (
        <div style={sty.container}>
            <button
                className={className}
                disabled={disabled}
                style={styx(sty.button, buttonStyle)}
                type="button"
                title="Upload images, videos, or documents"
                onClick={() => setShowDropdown(!showDropdown)}
            >
                <Icon
                    className="assistantChatUploadIcon"
                    size={20}
                    icon="upload"
                    color={Colors.purpleLight}
                />
            </button>

            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className="uploadDropdown"
                    style={sty.dropdown}
                >
                    <button
                        className="uploadDropdownOption"
                        style={mStyle(sty, "dropdownOption")}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = Colors.greyLighter;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={handleImagesVideosClick}
                    >
                        <Icon
                            className="dropdownIcon"
                            icon="image"
                            size={16}
                            color={Colors.greyDark}
                            style={sty.dropdownIcon}
                        />
                        Images/Videos
                    </button>

                    <button
                        className="uploadDropdownOption"
                        style={styx(
                            mStyle(sty, "dropdownOption"),
                            !isAuthenticated && sty.dropdownOption_disabled
                        )}
                        onMouseEnter={(e) => {
                            if (isAuthenticated) {
                                e.currentTarget.style.backgroundColor = Colors.greyLighter;
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={handleDocumentsClick}
                    >
                        <Icon
                            className="dropdownIcon"
                            icon="file"
                            size={16}
                            color={Colors.greyDark}
                            style={sty.dropdownIcon}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                            Documents
                            {!isAuthenticated && (
                                <span style={sty.loginRequired}>Login Required</span>
                            )}
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default UploadButtonWithDropdown;

const sty: Record<string, CSSProperties> = {
    container: {
        position: 'relative',
        display: 'inline-block'
    },
    button: {
        opacity: 1,
        cursor: 'pointer'
    },
    dropdown: {
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: 0,
        backgroundColor: Colors.white,
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 4,
        minWidth: 200,
        zIndex: 1000,
        animation: 'fadeInUp 0.2s ease-out'
    },
    dropdownOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
        fontSize: 14,
        color: Colors.greyDark,
        transition: 'background-color 0.15s ease',
        fontFamily: 'inherit'
    },
    dropdownOption_mobile: {
        padding: '8px'
    },
    dropdownOption_hover: {
        backgroundColor: Colors.greyLighter
    },
    dropdownOption_disabled: {
        cursor: 'default'
    },
    dropdownIcon: {
        marginRight: 8,
        flexShrink: 0
    },
    loginRequired: {
        fontSize: 12,
        color: Colors.blue,
        marginLeft: 4
    }
};